import React, { useState, useContext, useCallback, useEffect } from 'react'
import styled from 'styled-components'

import { useWithAuth } from '../../containers/App'
import { useNotifications } from '../../containers/Notifications'
import { normalizeSortState } from '../../components/Table/convertSortState'
import { AppContext } from '../../containers/App'
import { PlaceHolder } from '../../utils/withLoading'
import Search from '../Search'
import UsersModal from './UsersModal'
import type { User, ReadyProps } from './utils'

import Table from '../../components/Table'
import withLoading from '../../utils/withLoading'
import colors from '../../styles/colors'
import { disabilityOptions } from '../../containers/Patient/constants'

// @ts-ignore @todo add apprioprate declaration file
import userDiagnosedIcon from '../../images/userDiagnosedIcon.svg'
import { Hidden } from '../../styles/Shared'

function UnitPatientList({ currentRowUserData }) {
  const withAuth = useWithAuth()
  const { notify } = useNotifications()
  const [ready, setReady] = useState<ReadyProps>({
    patients: false,
    users: false,
  })
  const user = useContext<User>(AppContext)
  const [usersModalReady, setUsersModalReady] = useState({})
  const [prefetch, setPrefetch] = useState<boolean>(false)
  const [patientsData, setPatientsData] = useState({ models: null, meta: null })
  const [patientsState, setPatientsState] = useState({
    sorts: {},
    meta: {
      limit: 10,
      page: 0,
      total: 0,
    },
    filters: { search: '' },
  })

  const handleUserChange = useCallback(
    (vals) => {
      const changeUser = async () => {
        const result = await withAuth(
          `/diagnostic/rest/patient/${usersModalReady.id}`, //TODO fix typing
          {
            method: 'PUT',
            payload: {
              ...usersModalReady.person,
              disability: usersModalReady.disability,
              unitId: usersModalReady.unitId,
              school: usersModalReady.school,
              guardian: usersModalReady.guardian,
              targetDiagnosticianId: parseInt(vals.diagnosticianId),
            },
          }
        )
        result
          ? notify('Zmieniono diagnostę', 'success')
          : notify('Nie udało się zmienić diagnosty, spróbuj ponownie', 'error')
        handleChanges({})
      }
      changeUser()
    },
    [usersModalReady, withAuth, notify]
  )
  const onSearchChange = (search) => {
    setPatientsState((prevState) => ({
      ...prevState,
      meta: { ...prevState.meta, page: 0 },
      filters: { ...prevState.filters, search },
    }))
  }
  const fetchPatients = async () => {
    setReady({ ...ready, patients: false })
    const result = await withAuth(`/diagnostic/rest/patient/search`, {
      method: 'POST',
      payload: {
        filters: {
          ...patientsState.filters,
          unitId: user.unitId,
          diagnosticians: [currentRowUserData.id],
        },
        meta: {
          ...patientsState.meta,
          sorts: normalizeSortState(patientsState.sorts),
        },
      },
    })
    setReady({ ...ready, patients: true })
    return result
  }

  const onSortedChange = (sort) => {
    setPatientsState({ ...patientsState, sorts: sort })
  }
  const onPageChange = (index) => {
    setPatientsState({
      ...patientsState,
      meta: { ...patientsState.meta, page: index },
    })
  }

  const LoadingTable = withLoading(Table)
  const handleChanges = (value) => {
    setPrefetch(!prefetch)
    setUsersModalReady(value)
  }
  const fetchData = async () => {
    try {
      const resultPatients = await fetchPatients()
      setPatientsData(resultPatients)
    } catch {
      notify('Nie udało się pobrać użytkowników', 'error')
    }
  }

  useEffect(() => {
    fetchData()
  }, [patientsState, prefetch])
  return patientsData.models !== null && patientsData.meta !== null ? (
    <>
      <UsersModal
        onSubmit={handleUserChange}
        modalReady={usersModalReady}
        unitId={user.unitId}
        onClose={() => setUsersModalReady({})}
      ></UsersModal>
      <LoadingTable
        innerTable
        data={patientsData.models}
        page={patientsState.meta.page}
        total={patientsData.meta?.total}
        order={patientsState.sorts}
        ready={ready.patients}
        onSortedChange={(s) => onSortedChange(s)}
        onPageChange={(index) => onPageChange(index)}
        getLoadingProps={() => ({
          'aria-hidden': true,
          role: 'none',
        })}
        defaultPageSize={patientsState.meta.limit}
        getTdProps={() => ({
          style: { marginLeft: '10px' },
        })}
        getTheadTrProps={() => ({
          style: {
            display: 'flex',
            alignItems: 'stretch',
            fontVariantNumeric: 'lining-nums',
          },
        })}
        columns={[
          {
            Header: 'Data dołączenia',
            id: 'creationDate',
            sortable: false,
            accessor: ({ creationDate }) => `${creationDate}`,
            getProps: () => ({
              role: undefined,
            }),
          },
          {
            Header: 'Imię i nazwisko',
            id: 'person',
            sortable: true,
            accessor: ({ person }) => `${person.firstName} ${person.lastName}`,
            getProps: () => ({
              role: undefined,
            }),
          },
          {
            Header: 'Data Urodzenia',
            id: 'dateOfBirth',
            sortable: true,
            accessor: ({ person }) => `${person.dateOfBirth}`,
            getProps: () => ({
              role: undefined,
            }),
          },
          {
            Header: 'Płeć',
            sortable: true,
            id: 'gender',
            accessor: ({ person }) =>
              person.gender === 'Male' ? 'Męska' : 'Żeńska',
            getProps: () => ({
              role: undefined,
            }),
          },
          {
            Header: 'Grupa SPE',
            sortable: true,
            id: 'disability',
            accessor: ({ disability }) =>
              disabilityOptions.find((d) => d.value === disability)!.label,
            getProps: () => ({
              role: undefined,
            }),
          },

          {
            Header: (
              <React.Fragment>
                <div
                  aria-label="Akcje"
                  aria-live="polite"
                  style={{ width: '100%' }}
                >
                  <label>
                    <Search
                      placeholder="Szukaj"
                      input={{
                        onChange: onSearchChange,
                        value: '',
                      }}
                    />
                  </label>
                </div>
              </React.Fragment>
            ),
            id: 'actions',
            sortable: false,
            Cell: ({ original }) => (
              <IconButton
                onClick={() => setUsersModalReady(original)}
                type="button"
              >
                Przepisz do innego diagnosty
                <Img alt="" src={userDiagnosedIcon} aria-hidden="true" />
              </IconButton>
            ),
          },
        ]}
      />
    </>
  ) : (
    <PlaceHolder />
  )
}

export default UnitPatientList

const Img = styled.img`
  margin-bottom: 0;
`

const IconButton = styled.button`
  display: flex;
  margin: 0;
  border: 1px solid ${colors.light_black};
  &:hover {
    ${(props) => !props.disabled && `border: 1px solid ${colors.mantis_green};`}
  }
  &.danger {
    &:hover {
      border: 1px solid ${colors.red};
    }
  }
  align-items: center;
  cursor: ${(props) => !props.disabled && `pointer;`};
  height: auto;
  border-radius: 5px;
  > * {
    padding: 0 0 0 3px;
  }
  ${Img} {
    opacity: ${(props) => props.disabled && `50%`};
  }
  background: white;
`
